import type { View, ViewSubject } from "@views/viewsClient";

/**
 * Composing a view's identity for display (BE-2395).
 *
 * A view's `name` is the KIND of document ("Medical Note") and deliberately
 * repeats across every view of that kind, so it never identifies one on its own.
 * Two more parts finish the job: `subtitle` (which one) and the subject (whose).
 *
 * The subject is joined HERE rather than server-side because the same view wants
 * it in some places and not others: a search result must say "Jerry", the pane
 * already showing Jerry's folder must not, and a view fetched by uuid carries no
 * record of which surface asked for it.
 */

/** Just the parts of a view that make up its display identity, so callers
 * holding less than a whole `View` (the PDF export) can use these too. */
export type ViewIdentity = Pick<View, "name" | "subtitle" | "subjects">;

/**
 * The subject a view is named after. A view is usually about several (a note is
 * about the patient AND the visit); the patient is who staff know it by, so it
 * wins regardless of link order, and anything else falls back to the first.
 * Null when nothing nameable is linked.
 */
export const namingSubject = (view: ViewIdentity): ViewSubject | null => {
  const named = view.subjects.filter((s) => s.name);
  return named.find((s) => s.kind === "patient") ?? named[0] ?? null;
};

/**
 * Should a surface scoped to `surroundingSubjectId` name this view's subject?
 *
 * The question is "does the pane around this view already say whose it is",
 * which is NOT the same as "is the pane scoped to a subject at all": a client's
 * pane names the client, not the two pets whose notes it lists, and a room's
 * pane hosts a different patient every few hours. Comparing the view's own
 * naming subject against the surrounding one answers it per view - the only
 * case where the name is genuinely redundant is when they are the same subject.
 *
 * `null` is a clinic-wide surface, which never supplies a subject of its own.
 */
export const withSubjectFor = (
  view: ViewIdentity,
  surroundingSubjectId: string | null,
): boolean => namingSubject(view)?.id !== surroundingSubjectId;

/**
 * Who a view is about, in one phrase: the naming subject, and the client it
 * belongs to after a dash (WEB-3382).
 *
 * A signer approving an estimate needs both: the patient names the animal, the
 * client names who is billed, and clinics run several Lunas. Only a patient
 * subject carries a `clientName`, so every other kind reads as the bare name.
 */
const viewSubjectPhrase = (view: ViewIdentity): string | null => {
  const subject = namingSubject(view);
  if (!subject?.name) return null;
  // Same spelling as `PatientClientName`, which renders this pair everywhere
  // else. Command-K shows both a subject row and a view row in one dropdown, so
  // two spellings of one household would sit inches apart. That component can't
  // be reused here: this returns a plain string, for aria-labels and PDF text.
  return subject.clientName
    ? `${subject.name} – ${subject.clientName}`
    : subject.name;
};

/**
 * The line under a view's title: who it is about, then which one it is.
 *
 * `withSubject` is the caller's answer to "does my surface already say whose
 * view this is?" — false inside a subject's own pane, true in a clinic-wide list
 * or a search result. Either part may be missing (a singleton document carries
 * no subtitle), so this returns null rather than a stray separator when there is
 * nothing left to say.
 */
export const viewCaption = (
  view: ViewIdentity,
  { withSubject }: { withSubject: boolean },
): string | null => {
  const parts = [
    withSubject ? viewSubjectPhrase(view) : null,
    view.subtitle,
  ].filter((part): part is string => Boolean(part));
  return parts.length ? parts.join(" · ") : null;
};

/**
 * The words of a phrase, lowercased. Split on anything that is not a letter or
 * a number so punctuation never has to be typed exactly ("Luna's note"
 * tokenizes the same as "luna note"); single characters are dropped, since the
 * stray "s" left by a possessive would otherwise match nearly every view.
 *
 * Unicode-aware (`\p{L}\p{N}`, not `a-z0-9`): an ASCII-only class deletes a
 * Cyrillic or CJK name outright, leaving no tokens and no matches for a clinic
 * whose patients are not named in Latin script.
 */
const words = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((token) => token.length > 1);

/** One field with its punctuation removed rather than split on, so a token
 * typed without it ("omalley") still lands inside the word it belongs to. */
const squashed = (text: string): string =>
  text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");

/**
 * Does this view match a typed search term?
 *
 * Matched word by word against everything that identifies the view, rather than
 * as one string against each field in turn: a view's identity is SPLIT across
 * the title, the subtitle and the subject (BE-2395), so "luna note" is a
 * perfectly ordinary thing to type and no single field contains it. Every word
 * has to land somewhere, in any order.
 *
 * The subject's name is in the haystack because that is what staff remember a
 * document by; the title alone is the kind of document and would send "luna"
 * home empty.
 *
 * The data is normalized the same way the query is, so typing `omalley` finds
 * "O'Malley" and `jeanluc` finds "Jean-Luc". Normalizing only the query leaves
 * the punctuation in the data still having to be typed exactly, which is the
 * half staff cannot see. Fields stay separated so a token can't match across
 * two of them.
 */
export const viewMatchesQuery = (
  view: ViewIdentity,
  query: string,
): boolean => {
  const tokens = words(query);
  if (!tokens.length) return false;
  const haystack = [
    view.name,
    view.subtitle,
    ...view.subjects.flatMap((s) => [s.name, s.clientName]),
  ]
    .filter((field): field is string => Boolean(field))
    .map(squashed)
    .join(" ");
  return tokens.every((token) => haystack.includes(token));
};
